import { css } from '@emotion/react'
import { Cell, FormControl, Grid, Heading, HFlow, Tag, Text, Tooltip, VFlow } from 'bold-ui'
import { useAlert } from 'components/alert'
import { Box } from 'components/Box'
import { Form, FormRenderProps, PasswordField, SubmitButton, TextField } from 'components/form'
import { InfoIcon } from 'components/InfoIcon'
import { confirm } from 'components/modals/confirm'
import { useAlterarConfiguracaoAssinaturaDigitalMutation } from 'graphql/hooks.generated'
import { useAnalytics } from 'hooks/analytics/useAnalytics'
import { useMemo } from 'react'
import { isHttpsConnection } from 'util/https'
import { metaPath } from 'util/metaPath'

import { ConfiguracaoAssinaturaDigitalModel } from './model-assinaturaDigital'
import { validate } from './validator-assinaturaDigital'

interface ConfiguracaoAssinaturaDigitalProps {
  internetHabilitada: boolean
  assinaturaDigitalHabilitado: boolean
  usuario?: string
  senha?: string

  load(): void
}

const meta = metaPath<ConfiguracaoAssinaturaDigitalModel>()

export function ConfiguracaoAssinaturaDigital(props: Readonly<ConfiguracaoAssinaturaDigitalProps>) {
  const { internetHabilitada, assinaturaDigitalHabilitado, usuario, senha, load } = props

  const { analytics } = useAnalytics()
  const alert = useAlert()
  const [alterarConfiguracaoAssinaturaDigital] = useAlterarConfiguracaoAssinaturaDigitalMutation()
  const habilitarAssinaturaDigitalDisabled = !internetHabilitada || !isHttpsConnection

  const submitAlteracao = async (values: ConfiguracaoAssinaturaDigitalModel, msg: string) => {
    await alterarConfiguracaoAssinaturaDigital({
      variables: {
        input: {
          habilitado: !assinaturaDigitalHabilitado,
          usuario: values.usuario,
          senha: values.senha,
        },
      },
    })
    alert('success', msg)
    load()
  }

  const handleSubmit = async (formValues: ConfiguracaoAssinaturaDigitalModel) => {
    if (assinaturaDigitalHabilitado) {
      const confirmed = await confirm({
        title: 'Deseja desabilitar a assinatura digital?',
        confirmLabel: 'Desabilitar',
      })()

      if (confirmed) {
        analytics.logEvent('assinaturaDigital_desabilitado')
        await submitAlteracao(formValues, 'Integração com o serviço de assinatura digital desabilitada com sucesso.')
      }
    } else {
      analytics.logEvent('assinaturaDigital_habilitado')
      await submitAlteracao(formValues, 'Integração com o serviço de assinatura digital habilitada com sucesso.')
    }
  }

  const renderForm = (formProps: FormRenderProps<ConfiguracaoAssinaturaDigitalModel>) => (
    <Box>
      <VFlow>
        <HFlow justifyContent='space-between'>
          <Heading level={3} component='span'>
            <HFlow hSpacing={0.5} alignItems='center'>
              Assinatura digital
              <InfoIcon
                icon='infoCircleFilled'
                text='Para habilitar a assinatura digital, é necessário solicitar as credenciais abaixo. A instalação deve utilizar HTTPS.'
                style={css`
                  max-width: 20rem;
                `}
              />
            </HFlow>
          </Heading>

          {assinaturaDigitalHabilitado ? (
            <Tag type='success' icon='checkCircleFilled'>
              Habilitado
            </Tag>
          ) : (
            <Tag type='normal' icon='banFilled'>
              Desabilitado
            </Tag>
          )}
        </HFlow>

        <Text>
          Ao habilitar essa funcionalidade, é ativada a integração do sistema com o serviço de assinaturas digitais,
          possibilitando a ativação das funcionalidades que necessitam de assinatura digital.
        </Text>

        {!assinaturaDigitalHabilitado ? (
          <Grid>
            <Cell size={4}>
              <TextField label='Login' name={meta.usuario} required disabled={habilitarAssinaturaDigitalDisabled} />
            </Cell>

            <Cell size={4}>
              <PasswordField label='Senha' name={meta.senha} required disabled={habilitarAssinaturaDigitalDisabled} />
            </Cell>

            <Cell size={4}>
              <FormControl label={<span>&nbsp;</span>}>
                <Tooltip text={getHabilitarDisableTooltip(internetHabilitada, isHttpsConnection)}>
                  <SubmitButton
                    kind='primary'
                    size='small'
                    handleSubmit={formProps.handleSubmit}
                    disabled={habilitarAssinaturaDigitalDisabled}
                  >
                    Habilitar
                  </SubmitButton>
                </Tooltip>
              </FormControl>
            </Cell>
          </Grid>
        ) : (
          <SubmitButton kind='primary' size='small' handleSubmit={formProps.handleSubmit}>
            Desabilitar
          </SubmitButton>
        )}
      </VFlow>
    </Box>
  )

  const initialValues = useMemo(() => ({ usuario, senha }), [usuario, senha])

  return (
    <Form<ConfiguracaoAssinaturaDigitalModel>
      onSubmit={handleSubmit}
      initialValues={initialValues}
      validate={validate}
      render={renderForm}
    />
  )
}

function getHabilitarDisableTooltip(internetHabilitada: boolean, isHttpsConnection: boolean): string {
  if (!internetHabilitada) return 'Não pode ser habilitada sem conexão com a internet.'
  if (!isHttpsConnection)
    return 'Para habilitar o serviço de assinatura digital, é necessário que a instalação utilize o protocolo HTTPS.'
}
